import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  ArrowRight, 
  Award, 
  BookOpen, 
  Compass, 
  Heart, 
  GraduationCap, 
  Utensils, 
  ShieldCheck, 
  Users, 
  Sparkles, 
  MapPin, 
  Phone, 
  Clock, 
  Calendar,
  Sprout,
  ChefHat,
  Globe,
  Crown,
  Trophy,
  Home,
  Volume2,
  VolumeX,
  ShoppingCart,
  ExternalLink
} from "lucide-react";
import useLenis, { scrollToId } from "@/hooks/useLenis";
import { FadeUp } from "@/components/veedu/Shared";
import { CONTACT, FOUNDER_BOOK } from "@/veedu/data";
import { useAmbientAudio } from "@/context/AmbientAudioContext";
import { useReservation } from "@/context/ReservationContext";

const EASE = [0.16, 1, 0.3, 1];

const STORY_CHAPTERS = [
  { id: "origins", label: "Origins in Kerala" },
  { id: "culinary-path", label: "The Decision" },
  { id: "london", label: "Move to the UK" },
  { id: "career", label: "Indian Cuisine & Lalit" },
  { id: "leadership", label: "Leadership" },
  { id: "recognition", label: "Recognition" },
  { id: "the-book", label: "The Book: ATTITUDE" },
  { id: "veedu-dream", label: "The Dream of #VEEDU" },
  { id: "philosophy", label: "Food Philosophy" },
  { id: "timeline", label: "The Journey" },
  { id: "why-veedu", label: "Why It Matters" },
];

const ChefJomonPage = () => {
  useLenis();
  const { isPlaying, isMuted, toggleMute } = useAmbientAudio();
  const { openReservationModal } = useReservation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Chef Jomon — Founder & Culinary Story | Chef Jomon's #VEEDU";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "The story of Chef Jomon — From Kerala to London, former Executive Chef at The LaLiT London and founder of Chef Jomon's #VEEDU in Hornchurch."
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-ink text-cream selection:bg-brass selection:text-ink relative overflow-hidden">
      {/* Background Ambience & Warm Lighting */}
      <div className="fixed inset-0 bg-material-dark opacity-35 pointer-events-none z-0" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-brass/10 blur-[180px] pointer-events-none z-0" />

      {/* Top Floating Editorial Header */}
      <header className="sticky top-0 z-50 bg-[#0e0906]/90 backdrop-blur-md border-b border-brass/20 px-4 sm:px-8 py-3.5 sm:py-4 transition-all">
        <div className="max-w-[1340px] mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 text-cream/80 hover:text-brass transition-colors group"
            data-testid="back-to-home-link"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-brass/30 bg-brass/10 group-hover:border-brass group-hover:scale-105 transition-all">
              <ArrowLeft size={15} className="text-brass" />
            </div>
            <span className="font-sans text-[11px] sm:text-xs tracking-[0.2em] uppercase font-medium">
              Return to #VEEDU
            </span>
          </Link>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Ambient Music Toggle Icon */}
            <button
              type="button"
              onClick={toggleMute}
              data-testid="chef-ambient-audio-btn"
              aria-label={isMuted ? "Unmute meditation ambience" : "Mute meditation ambience"}
              title={isMuted ? "Unmute meditation ambience" : "Mute meditation ambience"}
              className="flex items-center justify-center w-8 h-8 rounded-full border border-brass/30 bg-black/40 hover:border-brass hover:bg-brass/10 text-cream/80 hover:text-brass transition-all duration-300 active:scale-95 cursor-pointer shadow-sm"
            >
              {isPlaying && !isMuted ? (
                <Volume2 className="w-3.5 h-3.5 text-brass animate-pulse" />
              ) : (
                <VolumeX className="w-3.5 h-3.5 text-cream/40" />
              )}
            </button>

            <button
              type="button"
              onClick={() => openReservationModal()}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brass/40 bg-brass/10 text-brass text-[11px] font-sans tracking-[0.15em] uppercase hover:bg-brass hover:text-ink transition-all font-medium cursor-pointer"
            >
              <span>Reserve Table</span>
            </button>
            <span className="font-display text-sm tracking-[0.18em] text-cream/90">#VEEDU</span>
          </div>
        </div>
      </header>

      {/* Quick Story Chapter Navigator (Horizontal Pill Rail) */}
      <nav aria-label="Story Chapters" className="relative z-20 border-b border-cream/10 bg-[#120B07]/80 backdrop-blur px-4 sm:px-8 py-2.5 overflow-x-auto no-scrollbar">
        <div className="max-w-[1340px] mx-auto flex items-center gap-2 whitespace-nowrap min-w-max text-[10px] sm:text-[11px] font-sans tracking-[0.14em] uppercase text-cream/60">
          <span className="text-brass font-medium mr-2 flex items-center gap-1.5">
            <BookOpen size={13} /> CHAPTERS:
          </span>
          {STORY_CHAPTERS.map((ch, idx) => (
            <button
              key={ch.id}
              onClick={() => scrollToId(ch.id)}
              className="px-3 py-1 rounded-full border border-brass/20 bg-charcoal/30 hover:border-brass/70 hover:text-cream hover:bg-brass/10 transition-all cursor-pointer"
            >
              {idx + 1}. {ch.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ─────────────────────────────────────────────────────────────
          1. HERO & EDITORIAL MAJESTY
      ───────────────────────────────────────────────────────────── */}
      <section className="relative z-10 pt-12 sm:pt-16 md:pt-20 pb-16 md:pb-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15">
        <div className="max-w-[1240px] mx-auto">
          {/* Super-Header Badge */}
          <FadeUp className="flex flex-wrap items-center gap-3 mb-6">
            <span className="px-3 py-1 rounded-full border border-brass/40 bg-brass/10 text-brass font-sans text-[10.5px] tracking-[0.25em] uppercase font-semibold">
              The Founder's Journey
            </span>
            <span className="text-brass/60">•</span>
            <span className="font-mal text-sm sm:text-base text-cream/70 tracking-wider">
              ഷെഫ് ജോമോൻ
            </span>
          </FadeUp>

          {/* Title & Subtitle */}
          <FadeUp delay={0.1}>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-normal text-cream tracking-tight leading-[1.05] max-w-5xl">
              CHEF JOMON
              <span className="block text-brass/90 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display italic font-normal tracking-normal mt-3 sm:mt-4 leading-tight">
                From Kerala to London — A Culinary Journey Built on Passion, Perseverance & Purpose
              </span>
            </h1>
          </FadeUp>

          {/* Filigree Ornamental Rule */}
          <FadeUp delay={0.2} className="flex items-center gap-4 my-8 md:my-10 max-w-xl">
            <span className="h-px flex-1 bg-gradient-to-r from-brass to-transparent" />
            <span className="text-xs text-brass select-none">❖</span>
            <span className="h-px flex-1 bg-gradient-to-l from-brass to-transparent" />
          </FadeUp>

          {/* Two-Column Hero Showcase: Portrait Card & Introductory Manifesto */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left: Chef Portrait with Framing & Badges */}
            <div className="lg:col-span-5">
              <FadeUp delay={0.25} className="relative mx-auto max-w-[420px]">
                <div className="relative rounded-2xl p-3 bg-gradient-to-b from-brass/40 via-brass/15 to-brass/40 shadow-[0_25px_60px_rgba(0,0,0,0.85)] border border-brass/40">
                  <div className="relative rounded-xl overflow-hidden aspect-[4/5] bg-charcoal group">
                    <img
                      src="/Images/chef-jomon-portrait.jpg"
                      alt="Chef Jomon - Founder and Executive Chef"
                      className="w-full h-full object-cover object-top filter brightness-[1.02] contrast-[1.05] transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#160F0B] via-[#160F0B]/25 to-transparent" />

                    {/* Signature overlay card */}
                    <div className="absolute inset-x-0 bottom-0 p-5 text-left bg-gradient-to-t from-ink via-ink/90 to-transparent">
                      <div className="bg-cream/10 backdrop-blur-md border border-brass/30 rounded-lg p-3 inline-block shadow-lg">
                        <img
                          src="/Images/Chef Jomon Text.png"
                          alt="Chef Jomon signature"
                          className="h-7 w-auto object-contain filter invert opacity-95"
                        />
                      </div>
                      <p className="mt-2.5 font-sans text-[11px] tracking-[0.2em] uppercase text-brass font-medium">
                        Chef Jomon
                      </p>
                      <p className="font-body text-[12px] text-cream/75">
                        Founder & Culinary Director, Chef Jomon's #VEEDU
                      </p>
                    </div>
                  </div>
                </div>

                {/* Key Career Anchors Box */}
                <div className="mt-5 grid grid-cols-3 gap-2.5 text-center">
                  <div className="p-3 rounded-xl border border-brass/20 bg-charcoal/40 backdrop-blur-sm">
                    <div className="font-display text-lg sm:text-xl text-brass font-semibold">20+</div>
                    <div className="font-sans text-[9px] uppercase tracking-wider text-cream/70 mt-0.5">Years Craft</div>
                  </div>
                  <div className="p-3 rounded-xl border border-brass/20 bg-charcoal/40 backdrop-blur-sm">
                    <div className="font-display text-lg sm:text-xl text-brass font-semibold">The LaLiT</div>
                    <div className="font-sans text-[9px] uppercase tracking-wider text-cream/70 mt-0.5">London Exec</div>
                  </div>
                  <div className="p-3 rounded-xl border border-brass/20 bg-charcoal/40 backdrop-blur-sm">
                    <div className="font-display text-lg sm:text-xl text-brass font-semibold">6 Years</div>
                    <div className="font-sans text-[9px] uppercase tracking-wider text-cream/70 mt-0.5">Veedu Dream</div>
                  </div>
                </div>

                {/* Published Memoir Callout */}
                <a
                  href="#the-book"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId("the-book");
                  }}
                  className="mt-3 flex items-center justify-between p-3 rounded-xl border border-brass/40 bg-gradient-to-r from-brass/20 via-charcoal/60 to-brass/10 hover:border-brass hover:bg-brass/20 transition-all text-left group shadow-lg"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brass text-ink font-bold shrink-0 shadow">
                      <BookOpen size={14} />
                    </div>
                    <div>
                      <div className="font-sans text-[9.5px] uppercase tracking-wider text-brass font-bold flex items-center gap-1.5">
                        <span>Author of A.T.T.I.T.U.D.E</span>
                        <span className="text-[8.5px] px-1.5 py-0.2 rounded bg-brass/20 text-cream/90 font-normal">EVEME</span>
                      </div>
                      <div className="font-body text-[11px] text-cream/80 line-clamp-1">
                        A Food Tale from God’s Own Country
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] font-sans uppercase tracking-widest text-brass font-semibold group-hover:translate-x-0.5 transition-transform shrink-0 ml-2">
                    Explore →
                  </span>
                </a>
              </FadeUp>
            </div>

            {/* Right: Executive Summary & Opening Statement */}
            <div className="lg:col-span-7 space-y-6">
              <FadeUp delay={0.3}>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-cream font-normal leading-snug">
                  “Every dish has a story. Behind every authentic recipe lies family, tradition, celebration, memory and identity.”
                </h2>
              </FadeUp>

              <FadeUp delay={0.4} className="space-y-4 text-cream/85 font-body text-base sm:text-lg leading-relaxed font-light">
                <p>
                  For Chef Jomon, that story began in Kerala, South India. From a childhood immersed in the rich culinary rituals of his family home to leading high-profile London kitchens and representing Indian gastronomy on prestigious national television, his path has been defined by one constant: an uncompromising devotion to authentic flavours and the spirit of genuine hospitality.
                </p>
                <p>
                  Today, Chef Jomon is the founder of <strong className="text-cream font-semibold">Chef Jomon’s #VEEDU</strong> in Hornchurch, East London — a restaurant born from a six-year dream to create a place where the flavours, memories and soul of Kerala could meet the energy and diversity of modern India.
                </p>
                <p>
                  His journey spans almost two decades of rigorous training, professional sacrifice, culinary leadership, and an unwavering belief that food connects people across oceans and generations.
                </p>
              </FadeUp>

              <FadeUp delay={0.5} className="pt-2 flex items-center gap-4">
                <button
                  onClick={() => scrollToId("origins")}
                  className="inline-flex items-center gap-2 text-brass hover:text-cream font-sans text-xs tracking-[0.22em] uppercase font-semibold transition-colors group"
                >
                  <span>Begin the Story</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          2. THE BEGINNING — ROOTED IN KERALA
      ───────────────────────────────────────────────────────────── */}
      <section id="origins" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-[#140D09]/60">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 01</span>
              <span>•</span>
              <span className="font-mal text-sm">കേരളത്തിലെ വേരുകൾ</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              THE BEGINNING — ROOTED IN KERALA
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              Jomon’s connection to food was born in Kerala, South India — a land blessed with lush backwaters, fertile soil, towering coconut groves and an ancient spice trade that shaped world history.
            </p>
            <p>
              In his childhood home, food was never an afterthought or simply something to eat; it was the heartbeat of daily life. The air was continuously perfumed with the crackle of mustard seeds in hot coconut oil, fresh curry leaves torn straight from the garden, the gentle tang of crushed tamarind, and the warming hum of whole black pepper and roasted spices.
            </p>
            <p>
              His earliest understanding of food was formed by watching family meals take shape around the wood-fired hearth. From delicate coastal seafood curries simmered in clay chattis to rich, slow-stirred festival vegetarian dishes of the Onam Sadya served on emerald banana leaves, every recipe was an act of care, celebration and community.
            </p>
            <blockquote className="my-6 pl-5 border-l-2 border-brass/70 italic font-display text-xl sm:text-2xl text-ivory font-normal leading-snug">
              “The flavours of a Kerala home do not leave you. They become your internal compass — a standard of depth, balance, and warmth that you seek in every dish you ever cook thereafter.”
            </blockquote>
            <p>
              Those sensory memories — the warmth of freshly pressed coconut milk, the scent of crushed kanthari chillies, and the joy of a table surrounded by loved ones — became the bedrock of everything Chef Jomon would build across the next two decades.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          3. THE DECISION TO BECOME A CHEF
      ───────────────────────────────────────────────────────────── */}
      <section id="culinary-path" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 02</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><GraduationCap size={14} /> Formal Foundations</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              THE DECISION TO BECOME A CHEF
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              What began as childhood curiosity soon turned into a life-defining vocation. Jomon made the conscious decision to pursue cooking not merely as a job, but as a disciplined professional craft requiring formal training and unwavering dedication.
            </p>

            {/* Academic Credential Highlight Box */}
            <div className="my-7 p-6 sm:p-7 rounded-xl border border-brass/40 bg-gradient-to-r from-brass/15 via-charcoal/80 to-brass/10 shadow-xl">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-brass/50 bg-brass/20 text-brass">
                  <GraduationCap size={24} />
                </div>
                <div>
                  <span className="font-sans text-[11px] tracking-[0.2em] uppercase text-brass font-semibold block">
                    Higher Culinary Education
                  </span>
                  <h3 className="font-display text-xl sm:text-2xl text-cream font-medium mt-1">
                    Bachelor’s Degree in Hotel Management
                  </h3>
                  <p className="font-body text-sm sm:text-base text-cream/80 mt-1">
                    Sarosh Institute of Hotel Administration, Mangalore University
                  </p>
                </div>
              </div>
            </div>

            <p>
              At the Sarosh Institute, Jomon immersed himself in the science, craft and discipline of commercial hospitality. Here, he mastered the fundamentals that would sustain his long career: the strict kitchen hierarchy, knife work, food safety regulations, ingredient preservation, cost efficiency, and the exacting demands of consistency under pressure.
            </p>
            <p>
              It was during these formative years that he developed a deep respect for technique. He learned that passion without discipline cannot sustain a great kitchen, and that consistency is the truest hallmark of culinary excellence.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          4. THE MOVE TO THE UNITED KINGDOM
      ───────────────────────────────────────────────────────────── */}
      <section id="london" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-[#140D09]/60">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 03</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Compass size={14} /> New Horizons</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              THE MOVE TO THE UNITED KINGDOM
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              Armed with his degree and kitchen fundamentals, Chef Jomon took the bold leap across continents, moving to the United Kingdom to test himself in one of the world’s most competitive, dynamic culinary capitals.
            </p>
            <p>
              Arriving in the UK meant adapting to an entirely new kitchen culture, climate, language nuances and fast-paced operational standards. In those early British years, there were no shortcuts. His trajectory was forged through sheer perseverance: 14-hour shifts, high-pressure services, cold winter mornings, and endless hours on the line mastering the precision demanded by top British hospitality establishments.
            </p>
            <p>
              Instead of being intimidated by the challenges, Chef Jomon treated every station as a classroom. He absorbed British produce standards, European kitchen philosophies, and modern service mechanics while staying fiercely true to his Indian spice heritage. His reputation for punctuality, calmness under extreme pressure, and palate precision quickly earned him the respect of head chefs and kitchen brigades across London.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          5. BUILDING A CAREER IN INDIAN CUISINE
      ───────────────────────────────────────────────────────────── */}
      <section id="career" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 04</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Utensils size={14} /> Culinary Progression</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              BUILDING A CAREER IN INDIAN CUISINE
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              Over the next decade, Chef Jomon rose through the ranks of London’s high-end dining scene. He was determined to challenge outdated perceptions of Indian food in Britain. To him, Indian cuisine was not a monolithic collection of generic curry sauces; it was a continent of ancient culinary micro-regions, intricate spicing techniques, coastal elegance, and royal heritage recipes.
            </p>
            <p>
              He developed a signature culinary style that brought together vibrant Southern Indian flavour profiles, pristine British seafood and meats, and sophisticated contemporary presentation.
            </p>

            {/* Major Milestone Banner: Executive Chef at The LaLiT London */}
            <div className="my-8 p-6 sm:p-8 rounded-xl border border-brass/50 bg-charcoal/80 shadow-2xl relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-44 h-44 rounded-full bg-brass/15 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <span className="px-3 py-1 rounded-full border border-brass/40 bg-brass/10 text-brass text-[10px] font-sans tracking-[0.25em] uppercase font-semibold">
                  Career Milestone
                </span>
                <h3 className="font-display text-2xl sm:text-3xl text-cream font-medium mt-3">
                  Executive Chef at The LaLiT London
                </h3>
                <p className="font-display italic text-brass text-sm sm:text-base mt-1">
                  Leading one of London’s premier luxury boutique hotel restaurants near Tower Bridge
                </p>
                <p className="font-body text-sm sm:text-base text-cream/85 mt-3 leading-relaxed">
                  As Executive Chef at The LaLiT London, located within a historic Grade II listed Victorian school near Tower Bridge, Chef Jomon oversaw all culinary operations, fine dining menu architecture, private banquets, and a diverse international kitchen brigade. Under his culinary leadership, the restaurant gained wide critical acclaim for its refined, artfully plated pan-Indian and coastal regional menus.
                </p>
              </div>
            </div>

            <p>
              His time at The LaLiT solidified his standing as one of London’s foremost ambassadors of contemporary Indian culinary art, proving that heritage flavours could stand proudly alongside the finest international dining concepts.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          6. MORE THAN COOKING — LEADERSHIP
      ───────────────────────────────────────────────────────────── */}
      <section id="leadership" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-[#140D09]/60">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 05</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Users size={14} /> Team & Culture</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              MORE THAN COOKING — LEADERSHIP
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              For Chef Jomon, great cooking is only half of the equation. His central philosophy has always been that <strong className="text-cream font-semibold">great restaurants are built by great teams</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
              <div className="p-5 rounded-xl border border-brass/25 bg-charcoal/50">
                <div className="flex items-center gap-2.5 text-brass font-sans text-xs uppercase tracking-wider font-semibold mb-2">
                  <ShieldCheck size={16} /> Mentorship & Growth
                </div>
                <p className="font-body text-sm text-cream/80 leading-relaxed">
                  Dedicated to training young chefs, instilling pride in the craft, and creating a supportive kitchen environment where emerging talent can thrive without fear.
                </p>
              </div>

              <div className="p-5 rounded-xl border border-brass/25 bg-charcoal/50">
                <div className="flex items-center gap-2.5 text-brass font-sans text-xs uppercase tracking-wider font-semibold mb-2">
                  <Sparkles size={16} /> Operational Rigour
                </div>
                <p className="font-body text-sm text-cream/80 leading-relaxed">
                  Deep focus on kitchen hygiene, food safety, seasonal menu development, and precision timing to ensure that every guest experiences consistent excellence.
                </p>
              </div>
            </div>

            <p>
              Beyond the restaurant pass, Chef Jomon has been a frequent speaker, industry demonstrator and culinary mentor, appearing at food festivals, masterclasses, and hospitality summits, inspiring the next generation of culinary professionals.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          7. RECOGNITION & HONOURS
      ───────────────────────────────────────────────────────────── */}
      <section id="recognition" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 06</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Award size={14} /> Accolades</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              RECOGNITION
            </h2>
            <p className="text-cream/70 font-body text-sm sm:text-base mt-2">
              Industry honours earned through years of dedicated service to hospitality.
            </p>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          {/* Recognition Grid — STRICTLY ACCORDING TO SUPPLIED DETAILS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mt-8">
            <FadeUp delay={0.15} className="p-6 rounded-xl border border-brass/35 bg-charcoal/50 backdrop-blur-sm flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/15 text-brass">
                <Award size={20} />
              </div>
              <div>
                <h3 className="font-display text-lg text-cream font-medium">Craft Guild of Chefs Culinary Hero</h3>
                <p className="font-body text-xs sm:text-sm text-cream/75 mt-1 leading-relaxed">
                  Honoured by the UK's leading chefs' association in recognition of exceptional service, leadership and community dedication in hospitality.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.2} className="p-6 rounded-xl border border-brass/35 bg-charcoal/50 backdrop-blur-sm flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/15 text-brass">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-display text-lg text-cream font-medium">BBC MasterChef Appearances</h3>
                <p className="font-body text-xs sm:text-sm text-cream/75 mt-1 leading-relaxed">
                  Featured on prestigious BBC MasterChef-related broadcasts, showcasing Kerala flavours and expert Indian culinary technique to millions across Britain.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.25} className="p-6 rounded-xl border border-brass/35 bg-charcoal/50 backdrop-blur-sm flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/15 text-brass">
                <Award size={20} />
              </div>
              <div>
                <h3 className="font-display text-lg text-cream font-medium">National Chef of the Year Semi-Finalist</h3>
                <p className="font-body text-xs sm:text-sm text-cream/75 mt-1 leading-relaxed">
                  Recognised in the UK’s most fiercely contested culinary competition, evaluating technical mastery, creativity and palate precision.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.3} className="p-6 rounded-xl border border-brass/35 bg-charcoal/50 backdrop-blur-sm flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/15 text-brass">
                <Heart size={20} />
              </div>
              <div>
                <h3 className="font-display text-lg text-cream font-medium">British Malayali Community Recognition</h3>
                <p className="font-body text-xs sm:text-sm text-cream/75 mt-1 leading-relaxed">
                  Celebrated as a prominent cultural ambassador and culinary leader representing Kerala heritage and gastronomy across the United Kingdom.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.35} className="p-6 rounded-xl border border-brass/35 bg-charcoal/50 backdrop-blur-sm flex items-start gap-4 md:col-span-2">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brass/40 bg-brass/15 text-brass">
                <Award size={20} />
              </div>
              <div>
                <h3 className="font-display text-lg text-cream font-medium">Hospitality & Culinary Awards Nominations</h3>
                <p className="font-body text-xs sm:text-sm text-cream/75 mt-1 leading-relaxed">
                  Recognised by industry bodies for significant ongoing contributions to the UK hospitality landscape and the promotion of South Indian culinary heritage.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          7. THE MEMOIR — A.T.T.I.T.U.D.E (FOUNDER'S BOOK)
      ───────────────────────────────────────────────────────────── */}
      <section id="the-book" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-gradient-to-b from-[#140D09]/80 via-[#190F09] to-[#140D09]/80 overflow-hidden">
        {/* Subtle decorative glow */}
        <div className="absolute top-1/2 left-10 -translate-y-1/2 w-[350px] h-[350px] bg-brass/10 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#C4572D]/10 blur-[120px] pointer-events-none" />

        <div className="max-w-[1140px] mx-auto relative z-10">
          <FadeUp>
            <div className="flex flex-wrap items-center gap-2.5 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 07</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><BookOpen size={14} /> The Founder's Memoir</span>
              <span>•</span>
              <span className="font-mal text-sm tracking-normal">നമ്മുടെ സ്വന്തം സെലിബ്രിറ്റി ഷെഫ്</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-normal text-cream tracking-tight">
              A.T.T.I.T.U.D.E
              <span className="block text-brass/90 text-xl sm:text-2xl lg:text-3xl font-display italic font-normal tracking-normal mt-2">
                A Food Tale from God’s Own Country
              </span>
            </h2>
            <div className="h-0.5 w-20 bg-brass/60 my-6" />
          </FadeUp>

          {/* Book Showcase Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mt-8">
            {/* Left: 3D Book Mockup & Purchasing Info */}
            <div className="lg:col-span-5 flex flex-col items-center">
              <FadeUp delay={0.15} className="relative w-full max-w-[340px]">
                {/* 3D Book Frame */}
                <div className="relative group mx-auto">
                  {/* Outer Atmospheric Glow */}
                  <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-brass/30 via-brass/10 to-brass/30 blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Book Body with 3D Spine and Drop Shadow */}
                  <div className="relative rounded-xl overflow-hidden border-2 border-brass/50 bg-[#120B07] shadow-[0_25px_60px_rgba(0,0,0,0.9),0_0_30px_rgba(194,160,74,0.15)] transition-transform duration-500 group-hover:scale-[1.02]">
                    <img
                      src={FOUNDER_BOOK.cover}
                      alt={FOUNDER_BOOK.fullTitle}
                      className="w-full h-auto object-cover filter brightness-[1.03] contrast-[1.04]"
                    />
                    {/* Spine highlight overlay */}
                    <div className="absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-black/60 via-white/15 to-transparent pointer-events-none" />
                    
                    {/* Top-Right Badge: Exclusive on EVEME */}
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink/90 backdrop-blur-md border border-brass/50 text-brass text-[9.5px] font-sans tracking-[0.18em] uppercase font-bold shadow-lg">
                      <Sparkles size={11} className="text-brass shrink-0" />
                      <span>EVEME Exclusive</span>
                    </div>

                    {/* Bottom Price Strip */}
                    <div className="absolute inset-x-0 bottom-0 p-3.5 bg-gradient-to-t from-black/95 via-black/80 to-transparent flex items-center justify-between text-left">
                      <div>
                        <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-brass/80 block">Pre-Order Price</span>
                        <span className="font-display text-xl text-cream font-bold">{FOUNDER_BOOK.price}</span>
                      </div>
                      <span className="text-[9.5px] font-sans uppercase tracking-wider text-cream/70 px-2.5 py-1 rounded border border-cream/20 bg-charcoal/60">
                        CARNET
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pre-Order CTA Button */}
                <div className="mt-6 space-y-3 text-center w-full">
                  <a
                    href={FOUNDER_BOOK.preOrderUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="chef-book-preorder-btn"
                    className="inline-flex w-full h-[52px] sm:h-[56px] items-center justify-center gap-3 rounded-full font-sans text-xs sm:text-sm font-bold tracking-[0.2em] uppercase shadow-[0_12px_35px_rgba(194,160,74,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(194,160,74,0.45)] group cursor-pointer"
                    style={{
                      backgroundColor: "#C2A04A",
                      color: "#160F0B",
                      border: "1px solid #C2A04A",
                    }}
                  >
                    <ShoppingCart size={16} />
                    <span>Grab Your Copy on EVEME</span>
                    <ExternalLink size={14} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />
                  </a>

                  <p className="font-body text-[11.5px] text-cream/60 tracking-wider">
                    🛒👉 Pre-order your copy today on{" "}
                    <a
                      href={FOUNDER_BOOK.preOrderUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brass underline hover:text-cream transition-colors"
                    >
                      eveme.uk
                    </a>
                  </p>
                </div>
              </FadeUp>
            </div>

            {/* Right: Malayalam Editorial & English Translation */}
            <div className="lg:col-span-7 space-y-6">
              {/* Malayalam Introductory Callout */}
              <FadeUp delay={0.2} className="p-5 sm:p-6 rounded-2xl border border-brass/35 bg-gradient-to-r from-brass/15 via-charcoal/70 to-brass/10 backdrop-blur-md shadow-xl">
                <div className="flex items-start gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brass/50 bg-brass/20 text-brass">
                    <BookOpen size={18} />
                  </div>
                  <div>
                    <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-brass font-bold block mb-1">
                      EVEME അവതരിപ്പിക്കുന്നു
                    </span>
                    <p className="font-mal text-base sm:text-lg text-cream leading-relaxed font-normal">
                      {FOUNDER_BOOK.malayalamIntro}
                    </p>
                  </div>
                </div>
              </FadeUp>

              {/* Malayalam Full Prose Block */}
              <FadeUp delay={0.3} className="space-y-4 p-6 sm:p-7 rounded-2xl border border-brass/20 bg-charcoal/30 backdrop-blur-sm">
                <div className="font-mal text-base sm:text-[17px] text-cream/90 leading-[1.85] space-y-3 font-normal">
                  <p className="text-brass text-lg sm:text-xl font-medium">
                    ഇത് വെറുമൊരു ആത്മകഥയല്ല...
                  </p>
                  <p>
                    കേരളത്തിലെ ഒരു കൊച്ചു ഗ്രാമത്തിൽ വളർന്ന്.. അമ്മയുടെ പ്രാർത്ഥനയും ... ലക്ഷ്യ ബോധവും .. ആത്മവിശ്വാസവും കൊണ്ട്... ഉയരങ്ങൾ കീഴടക്കിയ ഒരു പച്ചയായ മനുഷ്യന്റെ അനുഭവങ്ങളുടെ നേർക്കാഴ്ച...
                  </p>
                  <p>
                    നമ്മളെ സ്വപ്നം കാണാൻ പഠിപ്പിക്കുന്ന.. ആ സ്വപനത്തിലേക്കുള്ള ദിശാബോധം നൽകുന്ന പുസ്തകം ...
                  </p>
                  <p className="italic text-cream font-medium">
                    ജീവിതത്തിൽ എന്തെങ്കിലുമൊക്കെ ആയിത്തീരണം എന്ന് ആഗ്രഹിക്കുന്ന നമ്മൾ ഓരോരുത്തരും വായിച്ചിരിക്കേണ്ട .. അനുഭവിച്ചറിയേണ്ട പുസ്തകം ...
                  </p>
                  <p className="text-brass/95 font-medium pt-1">
                    ജീവിതത്തെ മറ്റൊരു തലത്തിൽ എത്തിക്കാൻ നമ്മളെ പ്രചോദിപ്പിക്കുന്ന പുസ്തകം ഇപ്പോൾ EVEME യിൽ ലഭ്യമാണ്.
                  </p>
                </div>
              </FadeUp>

              {/* English Companion Perspective */}
              <FadeUp delay={0.4} className="border-l-2 border-brass/60 pl-5 space-y-2">
                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-brass font-semibold block">
                  English Reflection
                </span>
                <p className="font-body text-sm sm:text-base text-cream/80 leading-relaxed italic">
                  “{FOUNDER_BOOK.englishSummary}”
                </p>
              </FadeUp>

              {/* 3 Core Highlights */}
              <FadeUp delay={0.5} className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                {FOUNDER_BOOK.keyHighlights.map((hl, i) => (
                  <div key={i} className="p-3.5 rounded-xl border border-brass/20 bg-charcoal/40">
                    <div className="font-sans text-[10.5px] uppercase tracking-wider text-brass font-semibold">
                      {hl.title}
                    </div>
                    <p className="font-body text-[11.5px] text-cream/70 mt-1 leading-snug">
                      {hl.desc}
                    </p>
                  </div>
                ))}
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          8. THE DREAM OF #VEEDU
      ───────────────────────────────────────────────────────────── */}
      <section id="veedu-dream" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-gradient-to-b from-[#180E08] via-ink to-[#180E08]">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 08</span>
              <span>•</span>
              <span className="font-mal text-sm">വീട് — സ്വപ്നം യാഥാർത്ഥ്യത്തിലേക്ക്</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              THE DREAM OF #VEEDU
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-6 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              Even while running some of London’s most prestigious hotel kitchens, Chef Jomon carried a quiet, persistent dream in his heart for over six years: to build a restaurant that was completely his own.
            </p>

            {/* Emotional Highlight: Meaning of Veedu */}
            <div className="my-8 p-8 sm:p-10 rounded-2xl border-2 border-brass/45 bg-[#211610]/90 shadow-2xl text-center relative overflow-hidden">
              <div className="font-mal text-4xl sm:text-5xl text-brass mb-2">വീട്</div>
              <h3 className="font-display text-2xl sm:text-4xl text-cream font-normal">
                “Veedu” means “home” in Malayalam.
              </h3>
              <p className="max-w-2xl mx-auto mt-4 font-body text-sm sm:text-base text-cream/80 leading-relaxed">
                In Kerala, a home is not defined by brick and mortar. A home is the aroma of spices in the air, the sound of rain tapping on roof tiles, the warmth of the hearth, and people gathered around a shared table where nobody is a stranger.
              </p>
            </div>

            <p>
              #VEEDU is Chef Jomon’s interpretation of what home tastes like. It is a space where the soulful cooking of his Kerala childhood meets the breadth and vibrancy of pan-Indian cuisine, executed with contemporary culinary thinking.
            </p>
            <p>
              Finding the historic, centuries-old timber-framed building at 197–201 High Street in Hornchurch felt like destiny. Two rich heritages — historic English timber architecture and vibrant Kerala culinary soul — meeting together under one warm, welcoming roof.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          9. THE PHILOSOPHY BEHIND THE FOOD
      ───────────────────────────────────────────────────────────── */}
      <section id="philosophy" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-[#140D09]/60">
        <div className="max-w-[1020px] mx-auto text-center">
          <FadeUp>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brass/30 bg-brass/10 text-brass text-[10.5px] font-sans tracking-[0.2em] uppercase font-medium mb-6">
              <span>Chapter 09 · The Culinary Creed</span>
            </div>
          </FadeUp>

          {/* THE MANIFESTO QUOTE BLOCK */}
          <FadeUp delay={0.15}>
            <div className="p-8 sm:p-12 rounded-2xl border border-brass/50 bg-gradient-to-b from-charcoal/90 via-ink to-charcoal/90 shadow-[0_20px_50px_rgba(0,0,0,0.7)] relative overflow-hidden">
              <span className="text-4xl sm:text-5xl text-brass/40 font-display select-none block mb-4">“</span>
              <p className="font-display text-2xl sm:text-4xl lg:text-5xl text-ivory font-normal leading-[1.25] tracking-tight">
                Respect the ingredient.<br />
                <span className="text-brass font-medium">Respect the tradition.</span><br />
                Understand the technique.<br />
                <span className="text-terra italic">Then have the courage to create.</span>
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-brass/50" />
                <span className="font-sans text-xs tracking-[0.25em] uppercase text-brass font-semibold">
                  Chef Jomon
                </span>
                <span className="h-px w-12 bg-brass/50" />
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.25} className="mt-10 text-left max-w-3xl mx-auto space-y-4 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              Chef Jomon’s culinary philosophy rejects the false choice between strict conservatism and gimmicky fusion. To him, true authenticity does not mean refusing to evolve; rather, it requires understanding a dish so deeply that you know exactly how to refine its presentation without losing its soul.
            </p>
            <p>
              At #VEEDU, spices are toasted and ground daily according to traditional ratios. Coconut is treated with reverence. Fish is prepared with Kerala coastal techniques. Yet the lightness of touch, the texture balances, and the artful plating belong unmistakably to the modern world.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          10. FROM A YOUNG BOY TO A FOUNDER (Visual Timeline Progression)
      ───────────────────────────────────────────────────────────── */}
      <section id="timeline" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 10</span>
              <span>•</span>
              <span>Milestones</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              FROM A YOUNG BOY TO A FOUNDER
            </h2>
            <p className="text-cream/70 font-body text-sm sm:text-base mt-2">
              A chronological progression tracing two decades of craft, sacrifice and leadership.
            </p>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          {/* Clean, Restrained Visual Timeline with Creative Icons */}
          <div className="relative mt-12 space-y-8 sm:space-y-10 before:absolute before:top-4 before:bottom-4 before:left-5 sm:before:left-6 before:w-[2px] before:-translate-x-1/2 before:bg-gradient-to-b before:from-brass/50 before:via-brass/25 before:to-brass/50">
            {[
              {
                step: "01",
                icon: Sprout,
                title: "A Young Boy from Kerala",
                place: "Kerala, South India",
                desc: "Growing up surrounded by traditional home cooking, the spice gardens, the harvest festivals and the rich culinary heritage of God's Own Country.",
              },
              {
                step: "02",
                icon: GraduationCap,
                title: "Hotel Management Student",
                place: "Sarosh Institute, Mangalore University",
                desc: "Formal degree studies in hotel management, building the foundations of professional kitchen discipline, food science and hygiene.",
              },
              {
                step: "03",
                icon: ChefHat,
                title: "Young Chef",
                place: "Early Kitchen Brigades",
                desc: "Formative early professional kitchen experiences, long hours, mastering knife work and understanding high-volume commercial operations.",
              },
              {
                step: "04",
                icon: Globe,
                title: "Chef in the United Kingdom",
                place: "London, England",
                desc: "Relocating to the UK, adapting to British hospitality culture, perseverance through demanding shifts and mastering European kitchen standards.",
              },
              {
                step: "05",
                icon: Crown,
                title: "Executive Chef",
                place: "The LaLiT London",
                desc: "Leading fine dining pan-Indian culinary direction at the luxury boutique hotel, earning industry and media acclaim for refined regional cooking.",
              },
              {
                step: "06",
                icon: Trophy,
                title: "Mentor & Culinary Leader",
                place: "UK Hospitality & Television",
                desc: "Craft Guild of Chefs Culinary Hero, BBC MasterChef appearances, mentoring next-generation talent and speaking across the industry.",
              },
              {
                step: "07",
                icon: BookOpen,
                title: "Author — A.T.T.I.T.U.D.E",
                place: "Published with EVEME & CARNET",
                desc: "Releasing his inspirational memoir 'A.T.T.I.T.U.D.E – A Food Tale from God’s Own Country', sharing his story from rural Kerala to London.",
              },
              {
                step: "08",
                icon: Home,
                title: "Founder of #VEEDU",
                place: "197 High Street, Hornchurch",
                desc: "Fulfilling a six-year dream: opening Chef Jomon's #VEEDU, a home for Kerala cuisine, memory and hospitality in London.",
              },
            ].map((item, idx) => (
              <FadeUp key={item.step} delay={idx * 0.08} className="relative flex items-start gap-4 sm:gap-6 group">
                {/* Creative Icon Circle Node */}
                <div className="z-10 flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full border-2 border-brass/60 bg-ink text-brass shadow-[0_0_15px_rgba(194,160,74,0.18)] transition-all duration-300 group-hover:scale-110 group-hover:border-brass group-hover:bg-brass group-hover:text-ink group-hover:shadow-[0_0_22px_rgba(194,160,74,0.45)]">
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.8} />
                </div>

                {/* Content Card */}
                <div className="flex-1 p-5 sm:p-6 rounded-xl border border-brass/25 bg-charcoal/40 backdrop-blur-sm transition-all duration-300 group-hover:border-brass/60 group-hover:bg-charcoal/70">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] font-bold text-brass/70 tracking-widest uppercase">
                        {item.step}
                      </span>
                      <span className="h-1 w-1 rounded-full bg-brass/40" />
                      <h3 className="font-display text-lg sm:text-xl text-cream font-medium">
                        {item.title}
                      </h3>
                    </div>
                    <span className="font-sans text-[9.5px] uppercase tracking-wider text-brass font-medium whitespace-nowrap">
                      {item.place}
                    </span>
                  </div>
                  <p className="mt-2 font-body text-xs sm:text-sm text-cream/75 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          11. WHY #VEEDU MATTERS
      ───────────────────────────────────────────────────────────── */}
      <section id="why-veedu" className="relative z-10 py-16 md:py-24 px-6 sm:px-8 md:px-12 lg:px-16 border-b border-brass/15 bg-[#140D09]/60">
        <div className="max-w-[1020px] mx-auto">
          <FadeUp>
            <div className="flex items-center gap-3 text-brass font-sans text-xs tracking-[0.3em] uppercase font-medium mb-3">
              <span>Chapter 11</span>
              <span>•</span>
              <span className="font-mal text-sm">എന്തുകൊണ്ട് വീട്?</span>
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-normal text-cream tracking-tight">
              WHY #VEEDU MATTERS
            </h2>
            <div className="h-0.5 w-16 bg-brass/60 my-6" />
          </FadeUp>

          <FadeUp delay={0.1} className="space-y-5 text-cream/90 font-body text-base sm:text-lg leading-relaxed font-light">
            <p>
              To Chef Jomon, #VEEDU is not merely a commercial restaurant; it is the culmination of every kitchen he has ever stood in, every trial he navigated, every mentor who believed in him, and every sacrifice made along the way.
            </p>
            <p>
              It connects the childhood flavours of Kerala with nearly two decades of hard-won London culinary excellence. It honours the people who supported him during the hardest days, celebrates the richness of Indian regional cooking, and proves that perseverance and purpose can bring a six-year dream to life.
            </p>
            <p>
              When you walk through the doors of 197 High Street, you are not simply dining out. You are stepping into the story of a chef who carried the soul of his homeland across the sea, waiting for the right moment to share it with you.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          12. WELCOME HOME (Emotional Finale & Closing)
      ───────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 md:py-32 px-6 sm:px-8 md:px-12 lg:px-16 text-center bg-gradient-to-b from-[#160F0B] to-[#0A0503]">
        <div className="max-w-[880px] mx-auto">
          <FadeUp>
            <span className="font-mal text-3xl sm:text-4xl text-brass/90 block mb-2">സ്വാഗതം</span>
            <h2 className="font-display text-4xl sm:text-6xl md:text-7xl font-normal text-cream tracking-tight leading-tight">
              Welcome to Chef Jomon’s #VEEDU.<br />
              <span className="font-display italic text-brass font-medium">Welcome home.</span>
            </h2>
          </FadeUp>

          {/* Chef Signature Mark */}
          <FadeUp delay={0.2} className="my-8 flex justify-center">
            <div className="p-4 rounded-xl border border-brass/30 bg-charcoal/50 backdrop-blur-md inline-block shadow-2xl">
              <img
                src="/Images/Chef Jomon Text.png"
                alt="Chef Jomon signature"
                className="h-10 sm:h-12 w-auto object-contain filter invert opacity-95"
              />
              <p className="mt-1.5 font-sans text-[10px] tracking-[0.25em] uppercase text-brass/80 font-medium">
                Chef Jomon
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.3} className="max-w-xl mx-auto text-cream/80 font-body text-base leading-relaxed">
            <p>
              Join us at 197–201 High Street in Hornchurch for an unforgettable journey through the food tales of Kerala and modern India.
            </p>
          </FadeUp>

          {/* Action CTAs */}
          <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => openReservationModal()}
              data-testid="page-reserve-table-btn"
              className="inline-flex h-[54px] sm:h-[58px] px-8 sm:px-10 items-center justify-center gap-3 rounded-full font-sans text-[11.5px] sm:text-[12.5px] font-semibold tracking-[0.2em] uppercase shadow-[0_12px_35px_rgba(194,160,74,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl cursor-pointer"
              style={{
                backgroundColor: "#C2A04A",
                color: "#160F0B",
                border: "1px solid #C2A04A",
              }}
            >
              <Calendar size={15} />
              <span>Reserve a Table</span>
            </button>

            <Link
              to="/#table"
              data-testid="page-explore-menu-btn"
              className="inline-flex h-[54px] sm:h-[58px] px-7 sm:px-9 items-center justify-center gap-3 rounded-full font-sans text-[11.5px] sm:text-[12.5px] font-medium tracking-[0.2em] uppercase text-cream border border-brass/40 bg-charcoal/40 backdrop-blur-sm transition-all duration-300 hover:border-brass hover:bg-brass/10"
            >
              <Utensils size={15} />
              <span>Explore The Menu</span>
            </Link>

            <Link
              to="/"
              data-testid="page-return-house-btn"
              className="inline-flex h-[54px] sm:h-[58px] px-7 sm:px-9 items-center justify-center gap-2 rounded-full font-sans text-[11.5px] sm:text-[12.5px] font-medium tracking-[0.2em] uppercase text-cream/70 hover:text-cream border border-cream/20 transition-all duration-300 hover:border-cream/40"
            >
              <ArrowLeft size={15} />
              <span>Return to House</span>
            </Link>
          </FadeUp>

          {/* Quick Contact Line */}
          <div className="mt-12 pt-8 border-t border-cream/10 flex flex-wrap items-center justify-center gap-6 text-cream/60 font-body text-xs uppercase tracking-wider">
            <span className="flex items-center gap-1.5">
              <MapPin size={13} className="text-brass" /> 197–201 High Street, Hornchurch RM11 3XT
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Phone size={13} className="text-brass" /> {CONTACT.phone}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Clock size={13} className="text-brass" /> Tue–Sun Service
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChefJomonPage;
